'use client';

import { SessionProvider } from 'next-auth/react';
import type { Session } from 'next-auth';
import ReactGA from 'react-ga4';
import { PHProvider } from '@/providers/posthog';
import dynamic from 'next/dynamic';
import { ValidateSession } from '@/providers/session';
import { AuthProvider } from '@/components/Auth/AuthProvider';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { OnboardingBackfillErrorSync } from '@/components/OnboardingBackfillErrorSync';

// Mirror the same tracking gate used in layout.tsx — only initialize GA on
// builds that have a real NEXT_PUBLIC_GOOGLE_ANALYTICS value baked in.
const trackingEnabled =
  process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS &&
  !process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS.startsWith('placeholder_');

if (trackingEnabled) {
  ReactGA.initialize('G-DTJPZ7514V');
}

// Client-only tracking components - no SSR needed
const PostHogPageView = dynamic(() => import('@/providers/posthog-pageview'), {
  ssr: false,
});

// Visual feedback toolbar for AI coding agents: click an element, add a note,
// copy markdown carrying the real selector. Development only — the ternary is
// resolved at build time so the import never reaches a production bundle.
const Agentation =
  process.env.NODE_ENV === 'production'
    ? null
    : dynamic(() => import('agentation').then((m) => m.Agentation), {
        ssr: false,
      });

/**
 * `session` is resolved on the server by the root layout and handed straight to
 * `SessionProvider`.
 *
 * Without it, `useSession()` reports `status: 'loading'` for the whole server
 * render, because next-auth only learns the session by fetching
 * `/api/auth/session` from the browser. Every consumer that gates on
 * `status === 'authenticated'` therefore renders its loading branch server-side:
 * `DashboardLayoutClient` returns a bare spinner without `{children}`, and
 * `useUserLogged` returns null user and organization. That is what makes the
 * dashboard unable to server-render its content at all.
 *
 * Passing it here also removes the `/api/auth/session` round trip from first
 * paint, which previously gated every dashboard route.
 */
export function Providers({
  children,
  session,
}: {
  children: React.ReactNode;
  session: Session | null;
}) {
  return (
    <>
      <PHProvider>
        <SessionProvider session={session}>
          {/* Base UI opens a tooltip after 600ms, which on a dashboard of
              dense readings feels like the tooltip is broken: you land on a
              figure, wait, and move on before it answers. 120ms is past the
              threshold where a pointer crossing the element on its way
              somewhere else fires it, and short enough to read as part of the
              same gesture.

              A single provider, not a default on the trigger: nearest-provider
              wins in Base UI, so the four call sites that set their own delay
              keep it, and grouping means the second tooltip in a row opens
              instantly. */}
          <TooltipProvider delay={120}>
            <PostHogPageView />
            <OnboardingBackfillErrorSync />
            <ValidateSession />
            {children}
            <AuthProvider />
            <Toaster />
            {Agentation ? <Agentation /> : null}
          </TooltipProvider>
        </SessionProvider>
      </PHProvider>
    </>
  );
}
